import { LoyaltyEnabledProvider } from '@/hooks/use-loyalty-enabled';
import { ConsentPolicyProvider } from '@/hooks/use-consent-policy';
import { type ConsentPolicy, toConsentPolicy } from '@/lib/consent-policy';
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';

// React Router
import {
    type DataStrategyResult,
    isRouteErrorResponse,
    Links,
    Meta,
    type MetaDescriptor,
    type MiddlewareFunction,
    Navigate,
    Outlet,
    Scripts,
    ScrollRestoration,
    type ShouldRevalidateFunctionArgs,
    useMatches,
    useRevalidator,
    useRouteLoaderData,
} from 'react-router';
import type { Route } from './+types/root';

// Third-party libraries
import { createInstance, type i18n } from 'i18next';
import { I18nextProvider, useTranslation, initReactI18next } from 'react-i18next';
import { PageDesignerProvider } from '@salesforce/storefront-next-runtime/design/react/core';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import { dataStoreMiddlewareLazy, getGcpApiKeyLazy } from '@salesforce/storefront-next-runtime/data-store';
import {
    buildUrl,
    SiteProvider,
    siteContext,
    type Site,
    type Locale,
} from '@salesforce/storefront-next-runtime/site-context';

// Middlewares
import authMiddlewareServer, { getAuth as getAuthServer } from '@/middlewares/auth.server';
import { getPublicSessionData } from '@/middlewares/auth.utils';
import createBasketMiddleware, { basketResourceContext, type BasketSnapshot } from '@/middlewares/basket.server';
import shopperContextMiddlewareServer from '@/middlewares/shopper-context.server';
import shopperGeolocationMiddleware from '@/middlewares/shopper-geolocation.server';
import legacyRoutesMiddlewareClient from '@/middlewares/legacy-routes.client';
import {
    performanceMetricsMiddlewareClient,
    performanceMetricsMiddlewareServer,
} from '@/middlewares/performance-metrics';
import { appConfigMiddlewareServer } from '@/middlewares/app-config.server';
import { appConfigMiddlewareClient } from '@/middlewares/app-config.client';
import { ConfigProvider, getConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { devLocalhostRedirectMiddleware } from '@/middlewares/dev-localhost-redirect.server';
import { pageDesignerSiteContextMiddleware } from '@/middlewares/page-designer-site-context.server';
import { siteContextMiddleware } from '@/middlewares/site-context.server';
import { i18nextMiddleware } from '@/middlewares/i18next.server';
import { correlationMiddleware } from '@/middlewares/correlation.server';
import { requestOriginMiddleware } from '@/middlewares/request-origin';
import { getAppOrigin } from '@/lib/origin';
import { loggingMiddleware } from '@/middlewares/logging.server';
import { pageDesignerResolutionMiddleware } from '@/middlewares/page-designer-page-resolution.server';
import { siteUrlConfigMiddleware } from '@/middlewares/site-url-config.server';
import { modeDetectionMiddlewareServer, modeDetectionMiddlewareClient } from '@/middlewares/mode-detection';
import { maintenanceMiddleware } from '@/middlewares/maintenance.server';
import { securityHeadersMiddleware } from '@/middlewares/security-headers.server';
import { getSecurityNonce } from '@salesforce/storefront-next-runtime/security';
import { useSecurityNonceFromContext } from '@salesforce/storefront-next-runtime/security/react';
import { basicAuthMiddleware } from '@/middlewares/basic-auth.server';
import { errorCacheControlMiddleware } from '@/middlewares/error-cache-control.server';
import { prefetchCacheControlMiddleware } from '@/middlewares/prefetch-cache-control.server';
import { commerceDisabledMiddleware } from '@/middlewares/commerce-disabled.server';

// Providers
import AuthProvider from '@/providers/auth';
import BasketProvider from '@/providers/basket';
import { ComposeProviders } from '@/providers/compose-providers';
import { CorrelationProvider } from '@/providers/correlation';
import { correlationContext } from '@/lib/correlation';
import WishlistProvider from '@/providers/wishlist';
import LoyaltyProvider from '@/providers/loyalty';
import AnalyticsUserProfileProvider from '@/providers/analytics-user-profile';
import WishlistOverlayProvider from '@/providers/wishlist-overlay';
import CountrySelectorProvider from '@/providers/country-selector';
import { CountrySuggestion } from '@/components/country-selector/country-suggestion';
import { loadCountrySuggestion } from '@/lib/country-suggestion.server';
import type { CountryLookup } from '@/lib/country-suggestion';
import { CommerceFeedbackProvider } from '@/providers/commerce-feedback';

// Components
import { CommerceFeedbackToaster, ToasterTheme } from '@/components/toast';
import { ShoppingContextCookieSync } from '@/components/shopping-context-cookie-sync';
import { TrackingConsentSync } from '@/components/tracking-consent-sync';

// Hooks
import { useExecutePendingAction } from '@/hooks/use-execute-pending-action';
import { CommerceEnabledProvider } from '@/hooks/use-commerce-enabled';

// Lib/Utils
import type { PublicSessionData } from '@/lib/api/types';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { i18nextOnClient } from '@/i18n-client-init';
import { PageViewTracker } from '@/lib/analytics/page-view-tracker';
import { DataLayerTracker } from '@/lib/analytics/data-layer-tracker';
import { shouldSkipRevalidationForCartOrWishlistAndNewsletterActions } from '@/lib/should-revalidate';
import { getSitePreferences } from '@/lib/preferences.server';
import { resolveCustomerCareHrefs, type CustomerCareHrefs } from '@/lib/customer-care-links';
import { buildUrlFromContext } from '@/lib/url.server';
import { normalizeErpPieceUnitOfMeasure } from '@/lib/erp-unit-of-measure';
import { resolveGoogleTagManagerConfig, type GoogleTagManagerConfig } from '@/lib/google-tag-manager';
import {
    resolveZendeskWebWidgetConfig,
    serializeZendeskWebWidgetSettings,
    shouldHideZendeskWebWidget,
    syncZendeskWebWidgetVisibility,
    type ZendeskWebWidgetConfig,
} from '@/lib/zendesk-web-widget';
import { buildSeoMetaDescriptors } from '@/utils/seo';
import { buildSeoTitle, resolveErrorPageTitle } from '@/lib/page-seo';
import { DEFAULT_SITE_NAME_KEY } from '@/components/seo-meta';
import { isCommerceEnabled } from '@/lib/commerce-enabled.server';

// Assets
import favicon from '/favicon.ico';

// Fonts (preload the two variable WOFF2 families used by the stylesheet)
import HankenGrotesk from '@fonts/HankenGrotesk-VariableFont_wght.woff2?url';
import Lora from '@fonts/Lora-VariableFont_wght.woff2?url';

// Styles
import { PageDesignerInit } from '@/page-designer-init';
import appStylesHref from './style.css?url';

// Extensions
import { UITargetProviders } from '@/targets/ui-target-providers';
import { type Maintenance, isMaintenanceError, maintenanceContext } from '@/lib/maintenance';

// Layout Components - logo for error page
import logo from '/images/logo.svg';
import { resolveLoyaltySummaryForCustomer } from './lib/api/crm-loyalty.server';
import { resolveAnalyticsUserProfile } from './lib/analytics/analytics-user-profile.server';
import { getWishlists, type WishlistSummary } from './lib/api/wishlist.server';
import type { LoyaltySummary } from './types/loyalty';
import type { AnalyticsUserProfile } from './types/analytics-user-profile';

export const links: Route.LinksFunction = () => {
    return [
        // Preload critical fonts
        { rel: 'preload', href: HankenGrotesk, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        { rel: 'preload', href: Lora, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        { rel: 'preload', href: appStylesHref, as: 'style' },
        { rel: 'stylesheet', href: appStylesHref },
    ];
};

export const meta: Route.MetaFunction = ({ loaderData, error }) => {
    // Never canonicalize or index a failing URL. The error title lives here rather than in the
    // error views so it stays in `<head>` through hydration.
    if (error) {
        const status = isRouteErrorResponse(error) ? error.status : undefined;
        const title = loaderData && resolveErrorPageTitle(loaderData.errorTranslations, status);
        return [
            ...(title && loaderData ? [{ title: buildSeoTitle({ title, siteName: loaderData.seoSiteName }) }] : []),
            { name: 'robots', content: status === 404 ? 'noindex, nofollow' : 'noindex' },
        ];
    }
    return loaderData?.seoMeta ?? [];
};

export const middleware: MiddlewareFunction<Response>[] = [
    correlationMiddleware,
    errorCacheControlMiddleware,
    prefetchCacheControlMiddleware,
    requestOriginMiddleware,
    loggingMiddleware,
    basicAuthMiddleware,
    modeDetectionMiddlewareServer,
    appConfigMiddlewareServer,
    securityHeadersMiddleware,
    devLocalhostRedirectMiddleware,
    pageDesignerSiteContextMiddleware,
    siteContextMiddleware, // Must run after appConfig, before i18next and currency
    ...dataStoreMiddlewareLazy,
    commerceDisabledMiddleware,
    siteUrlConfigMiddleware, // Must run after siteContextMiddleware (entry key uses site id)
    i18nextMiddleware,
    pageDesignerResolutionMiddleware,
    performanceMetricsMiddlewareServer,
    maintenanceMiddleware,
    authMiddlewareServer,
    createBasketMiddleware(),
    shopperContextMiddlewareServer,
    shopperGeolocationMiddleware,
];

export const clientMiddleware: MiddlewareFunction<Record<string, DataStrategyResult>>[] = [
    // Client middleware functions have varying return types, but React Router expects Record<string, DataStrategyResult>
    // We cast through unknown to avoid type errors while maintaining runtime correctness
    appConfigMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Must run first to set config in context
    modeDetectionMiddlewareClient,
    legacyRoutesMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Checks hybrid.enabled, needs config from context
    performanceMetricsMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>,
];

export const loader = async ({
    context,
    request,
}: Route.LoaderArgs): Promise<{
    // Public auth data - only non-sensitive fields, safe to serialize
    clientAuth: PublicSessionData;
    appConfig: AppConfig;
    basketSnapshot: BasketSnapshot | null;
    /** Site-prefixed help center hrefs for the current locale, from the `customerCareLinks` site preference. */
    customerCareHrefs: CustomerCareHrefs;
    countrySuggestion: Promise<CountryLookup>;
    wishlistSummary: Promise<WishlistSummary[]>;
    loyaltySummary: Promise<LoyaltySummary | null>;
    loyaltyEnabled: boolean;
    consentPolicy: ConsentPolicy;
    analyticsUserProfile: Promise<AnalyticsUserProfile | null> | null;
    maintenance: Maintenance;
    locale: Locale;
    site: Site;
    currency: string;
    correlationId: string;
    pageDesignerMode: 'EDIT' | 'PREVIEW' | undefined;
    // Pre-computed in the loader (server-only) so seo.ts stays out of the client bundle
    seoMeta: MetaDescriptor[];
    // OOTB GCP Address Autocomplete API key sourced from the MRT data store
    gcpApiKeyFromDAL: string;
    // Market-specific ERP unit used for whole pieces
    giftCardConfig: import('@/hooks/use-gift-card-config').GiftCardConfig;
    erpPieceUnitOfMeasure: string;
    googleTagManager: GoogleTagManagerConfig | null;
    zendeskWebWidget: ZendeskWebWidgetConfig | null;
    commerceEnabled: boolean;
    giftCardPaymentEnabled: boolean;
    // Return as function to prevent i18next instance serialization
    getI18next: () => i18n;
    // Serialized error namespace for the active locale — used by ErrorBoundary on SSR/hydration
    errorTranslations: Record<string, unknown>;
    // Localized site name for the error `<title>` built in `meta`
    seoSiteName: string;
    // CSP nonce for inline scripts; null when security headers middleware is disabled
    nonce: string | null;
}> => {
    const session = getAuthServer(context);

    const appConfig = getConfig(context);

    // On the server side, our middleware stores the translations in this i18next object
    // so we'll need to be careful not to accidentally serialize this object (to avoid bloating the html).
    const { i18next } = getTranslation(context);

    // Get resolved site, locale, and currency from site context middleware
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        throw new Error('Site context not found. Ensure siteContextMiddleware runs before loaders.');
    }
    const { locale, site, currency } = siteCtx;

    // Load the application basket provider with the basket snapshot. We are actively not loading the basket, as
    // we want to lazy load the basket when the basket is needed. This prevents low-engagement users from causing
    // unnecessary resource usage in the form of basket creations.
    const basketSnapshot = context.get(basketResourceContext)?.snapshot ?? null;

    // Get correlation ID from middleware for request tracing
    const correlationId = context.get(correlationContext);

    // Get maintenance data from middleware
    const maintenance = context.get(maintenanceContext);

    // Extract only non-sensitive fields for client - tokens stay server-side only
    const clientAuth = getPublicSessionData(session);

    const requestUrl = new URL(request.url);
    const [sitePreferences, commerceEnabled, gcpApiKeyFromDAL] = await Promise.all([
        getSitePreferences(context),
        isCommerceEnabled(context),
        getGcpApiKeyLazy(context),
    ]);

    const isRegistered =
        session.userType === 'registered' &&
        session.customerId &&
        session.accessToken &&
        session.accessTokenExpiry &&
        session.accessTokenExpiry > Date.now();
    const wishlistSummary =
        commerceEnabled && isRegistered && session.customerId
            ? getWishlists(context, session.customerId)
            : Promise.resolve([]);
    const loyaltyEnabled = sitePreferences.loyaltyEnabled !== false;
    const loyaltySummary =
        loyaltyEnabled && commerceEnabled && isRegistered && session.customerId
            ? resolveLoyaltySummaryForCustomer(context, session.customerId)
            : Promise.resolve(null);
    const analyticsUserProfile =
        commerceEnabled && isRegistered && session.customerId
            ? resolveAnalyticsUserProfile(context, session.customerId)
            : null;

    const seoMeta = buildSeoMetaDescriptors({
        site,
        appConfig,
        // Use the resolved public origin (custom domain on MRT) rather than
        // requestUrl.origin (lambda-internal hostname on hybrid deployments)
        // so canonical/hreflang URLs match what the customer is actually browsing.
        origin: getAppOrigin(context),
        locale,
        location: { pathname: requestUrl.pathname, search: requestUrl.search },
    });

    // CSP nonce produced by securityHeadersMiddleware; null when middleware is disabled.
    const nonce = getSecurityNonce(context);

    return {
        appConfig,
        basketSnapshot,
        countrySuggestion: loadCountrySuggestion(request, context),
        customerCareHrefs: resolveCustomerCareHrefs(sitePreferences.customerCareLinks, locale.id, (path) =>
            buildUrlFromContext(path, context)
        ),
        wishlistSummary,
        loyaltySummary,
        loyaltyEnabled,
        consentPolicy: toConsentPolicy(sitePreferences),
        analyticsUserProfile,
        locale,
        site,
        currency,
        correlationId,
        maintenance,
        clientAuth,
        seoMeta,
        gcpApiKeyFromDAL,
        giftCardConfig: {
            providerId: sitePreferences.giftCardProvider === 'FISERV' ? ('FISERV' as const) : ('DOMEC' as const),
            pinRequired: sitePreferences.giftCardProvider === 'FISERV' && sitePreferences.fiservPinRequired !== false,
        },
        erpPieceUnitOfMeasure: normalizeErpPieceUnitOfMeasure(sitePreferences.erpPieceUnitOfMeasure),
        googleTagManager: resolveGoogleTagManagerConfig(sitePreferences),
        zendeskWebWidget: resolveZendeskWebWidgetConfig(sitePreferences),
        commerceEnabled,
        giftCardPaymentEnabled: sitePreferences.disableGiftCardPayment !== true,
        getI18next: () => i18next,
        errorTranslations: (i18next.getResourceBundle(i18next.language, 'routeError') as Record<string, unknown>) ?? {},
        seoSiteName: i18next.t(DEFAULT_SITE_NAME_KEY, { ns: 'common' }),
        pageDesignerMode: isDesignModeActive(request) ? 'EDIT' : isPreviewModeActive(request) ? 'PREVIEW' : undefined,
        nonce,
    };
};

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
    if (shouldSkipRevalidationForCartOrWishlistAndNewsletterActions(args)) {
        return false;
    }

    return args.defaultShouldRevalidate;
}

// This creates a union type where properties unique to either loader are optional
// Properties present in both loaders remain required
type ServerLoaderData = Awaited<ReturnType<typeof loader>>;
type LoaderData = ServerLoaderData;

export function Layout({ children }: PropsWithChildren) {
    const data = useRouteLoaderData<LoaderData>('root');
    const appConfig = data?.appConfig;
    const googleTagManager = data?.googleTagManager;
    const zendeskWebWidget = data?.zendeskWebWidget;
    const appConfigScript = appConfig
        ? `window.__APP_CONFIG__ = ${JSON.stringify(appConfig)
              .replace(/</g, '\\u003c')
              .replace(/>/g, '\\u003e')
              .replace(/&/g, '\\u0026')
              .replace(/\u2028/g, '\\u2028')
              .replace(/\u2029/g, '\\u2029')};`
        : '';
    // React 19 omits the attribute when the value is undefined, so coerce null/missing to undefined.
    // Falls back to the NonceContext so the error path (root loader threw → no
    // loader data) still gets a valid nonce on its inline scripts. The middleware
    // sets `securityContext` *before* `next()`, and `entry.server.tsx` wraps
    // the SSR tree with <NonceContext.Provider>, so the context is populated
    // even when the loader fails.
    const nonceFromContext = useSecurityNonceFromContext();
    const nonce = data?.nonce ?? nonceFromContext ?? undefined;

    const i18next = typeof window === 'undefined' ? data?.getI18next?.() : i18nextOnClient;
    const lang = i18next?.language ?? 'en';
    const dir = i18next?.dir(lang) ?? 'ltr';

    return (
        <html lang={lang} dir={dir}>
            <head>
                <meta charSet="utf-8" />
                {zendeskWebWidget?.settings && (
                    <script
                        data-zendesk-settings
                        nonce={nonce}
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{
                            __html: serializeZendeskWebWidgetSettings(zendeskWebWidget.settings),
                        }}
                    />
                )}
                {zendeskWebWidget && (
                    <script
                        id="ze-snippet"
                        data-zendesk-web-widget
                        nonce={nonce}
                        src={zendeskWebWidget.scriptUrl}
                        defer
                    />
                )}
                <link rel="icon" type="image/x-icon" href={favicon} />
                {appConfig?.links?.preconnect?.map((origin: string) => (
                    <link key={origin} rel="preconnect" href={origin} />
                ))}
                {appConfig?.links?.prefetchDns?.map((origin: string) => (
                    <link key={origin} rel="dns-prefetch" href={origin} />
                ))}
                {appConfig?.links?.prefetch?.map((href: string) => (
                    <link key={href} rel="prefetch" href={href} />
                ))}
                <script
                    nonce={nonce}
                    // Browsers strip the `nonce` content attribute from the DOM after
                    // applying CSP (so scripts can't read each other's nonce), leaving
                    // `nonce=""` in the client DOM while the server rendered the real
                    // value. On this hand-written inline script that surfaces as a
                    // hydration attribute mismatch. React's own <Scripts>/<ScrollRestoration>
                    // handle this internally; here we suppress the warning for the same
                    // browser behavior. The CSP nonce itself still applies correctly.
                    //
                    // Note: this also suppresses any server/client divergence of the
                    // __APP_CONFIG__ body, not just the nonce attribute. Safe today
                    // because appConfig is server-rendered and never mutated on the
                    // client. If client-side config mutation is ever introduced, revisit
                    // this — a real config mismatch would be silently hidden here.
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{
                        __html: `
                        window.dataLayer = window.dataLayer || [];
                        ${appConfigScript}
                    `,
                    }}
                />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
            </head>
            <body className="antialiased flex flex-col min-h-screen">
                {googleTagManager && (
                    <noscript>
                        <iframe
                            src={googleTagManager.noscriptUrl}
                            height="0"
                            width="0"
                            className="hidden invisible"
                            title="Google Tag Manager"
                        />
                    </noscript>
                )}
                {children}
                <ToasterTheme />
                <CommerceFeedbackToaster />
                <ScrollRestoration nonce={nonce} />
                <Scripts nonce={nonce} />
                {/* Dev-only overlay: mounts outside the React tree to avoid interfering with app state/context. Zero production overhead — tree-shaken by Vite when PROD=true. */}
                <UITargetDevModeInit />
            </body>
        </html>
    );
}

/**
 * Error page content component with i18n support.
 * When called without an I18nextProvider (early error before loader ran), t falls back to
 * the key string — callers must pass translated strings directly via props in that case.
 */
function ErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
    title,
    message,
    secondaryMessage,
    statusDetails,
    goToHomepageLabel,
    allRightsReservedLabel,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
    title: string;
    message?: string;
    secondaryMessage?: string;
    statusDetails?: string;
    goToHomepageLabel: string;
    allRightsReservedLabel: string;
}) {
    return (
        <>
            {/* Simple Header */}
            <header className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center gap-x-4 lg:gap-x-6 h-16">
                        <a href={homepageUrl} className="flex-shrink-0 flex items-center">
                            <img
                                src={logo}
                                alt="Logo"
                                className="h-3 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                            />
                        </a>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="grow pt-8">
                <div className="flex items-center justify-center min-h-[60vh] px-4 py-12">
                    <div className="mx-auto max-w-3xl w-full text-center">
                        {/* Large status code */}
                        {status && <div className="text-error-status font-bold leading-none mb-8">{status}</div>}

                        <h1 className="text-4xl md:text-5xl font-bold mb-6">{title}</h1>

                        {message && <p className="text-lg text-muted-foreground mb-4 max-w-2xl mx-auto">{message}</p>}
                        {secondaryMessage && (
                            <p className="text-base text-muted-foreground mb-4 max-w-2xl mx-auto">{secondaryMessage}</p>
                        )}
                        {statusDetails && (
                            <p className="text-sm text-muted-foreground mb-12 max-w-2xl mx-auto opacity-50">
                                {statusDetails}
                            </p>
                        )}

                        {/* For non-route errors: show technical details */}
                        {!status && details && (
                            <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">Error: {details}</p>
                        )}

                        {/* Back to home button */}
                        <div>
                            <a
                                href={homepageUrl}
                                className="inline-block rounded-none bg-primary px-12 py-3 text-base font-semibold text-primary-foreground no-underline transition-colors hover:bg-primary/90">
                                {goToHomepageLabel}
                            </a>
                        </div>

                        {/* Stack trace (only in dev mode with stack) */}
                        {stack && (
                            <div className="mt-16 border border-border rounded-none bg-muted/30 text-left">
                                <div className="flex items-center px-4 py-3 border-b border-border">
                                    <h2 className="text-sm font-semibold text-foreground">Stack Trace</h2>
                                </div>
                                <pre className="p-4 overflow-auto max-h-80 text-xs leading-relaxed text-foreground/90 font-mono">
                                    <code>{stack}</code>
                                </pre>
                                <div className="px-4 py-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground">
                                        To disable stack traces in production, turn off{' '}
                                        <code className="text-xs">unstable_devTools</code> in your router config.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Simple Footer */}
            <footer className="mt-auto bg-background border-t border-border">
                <div className="mx-auto max-w-7xl section-container py-8">
                    <p className="text-center text-sm text-muted-foreground">
                        © {new Date().getFullYear()} {allRightsReservedLabel}
                    </p>
                </div>
            </footer>
        </>
    );
}

/**
 * Renders the error page using translations from the root loader payload.
 * Requires an I18nextProvider wrapping it.
 */
function TranslatedErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
}) {
    const { t } = useTranslation('routeError');

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;

    if (status === 404) {
        title = t('404.title');
        message = t('404.message');
        secondaryMessage = t('404.secondaryMessage');
    } else if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={title}
            message={message}
            secondaryMessage={secondaryMessage}
            goToHomepageLabel={t('goToHomepage')}
            allRightsReservedLabel={t('allRightsReserved')}
        />
    );
}

export function ErrorBoundary({ error }: { error: unknown }) {
    // All hooks must be called unconditionally before any early returns.
    const rootData = useRouteLoaderData<typeof loader>('root');

    const language =
        (typeof window !== 'undefined' && i18nextOnClient ? i18nextOnClient.language : undefined) ||
        (typeof document !== 'undefined' ? document.documentElement.lang : undefined) ||
        rootData?.locale?.id ||
        'en';

    const errorTranslations = rootData?.errorTranslations;
    const fallbackLng = rootData?.appConfig.i18n.fallbackLng ?? 'en';

    // Only created when errorTranslations is available (root loader ran).
    // When the loader did not run (e.g. middleware crash), errorTranslations is undefined and we
    // skip i18next entirely — rendering hardcoded English strings directly avoids any static import
    // of locale JSON that would cause Vite to bundle/preload locale chunks on every page.
    const i18nextInstance = useMemo(() => {
        if (!errorTranslations || Object.keys(errorTranslations).length === 0) return null;
        const resources: Record<string, { routeError: Record<string, unknown> }> = {
            [fallbackLng]: { routeError: errorTranslations },
            [language]: { routeError: errorTranslations },
        };
        const instance = createInstance();
        void instance.use(initReactI18next).init({
            lng: language,
            fallbackLng,
            resources,
            interpolation: { escapeValue: false },
            initAsync: false,
        });
        return instance;
    }, [language, errorTranslations, fallbackLng]);

    const homepageUrl = rootData?.site
        ? buildUrl({
              to: '/',
              urlConfig: rootData.appConfig.url,
              params: {
                  siteId: rootData.site.alias ?? rootData.site.id,
                  localeId: rootData.appConfig.localeAliasMap?.[rootData.locale.id] ?? rootData.locale.id,
              },
          })
        : '/';

    // Redirect maintenance errors before rendering.
    if (isMaintenanceError(error) || (isRouteErrorResponse(error) && error.status === 503)) {
        return <Navigate to="/maintenance" replace />;
    }

    let status: number | undefined;
    let details: string | undefined;
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        status = error.status;
        details = error.statusText;
    } else if (error instanceof Error) {
        details = error.message;
        stack = error.stack;
    } else if (typeof error === 'string') {
        details = error;
    }

    // Root loader ran — render with active locale translations.
    if (i18nextInstance) {
        return (
            <I18nextProvider i18n={i18nextInstance}>
                <TranslatedErrorPageContent status={status} details={details} stack={stack} homepageUrl={homepageUrl} />
            </I18nextProvider>
        );
    }

    // Root loader did not run (e.g. middleware crash before loader executed).
    // No locale context available — render hardcoded English strings with no i18next dependency.
    const englishContent = {
        404: { title: 'Page not found', message: 'The requested page could not be found.' },
        403: { title: 'Access restricted', message: "You don't have permission to view this page." },
        500: { title: 'Something went wrong', message: 'An unexpected error occurred. Please try again later.' },
    };
    const fallbackContent =
        status && status in englishContent
            ? englishContent[status as keyof typeof englishContent]
            : { title: 'Something went wrong', message: undefined };

    // Root `meta` has no translations to build a title from here, so this view owns it.
    return (
        <>
            <title>{`${fallbackContent.title} | Eataly`}</title>
            <ErrorPageContent
                status={status}
                details={details}
                stack={stack}
                homepageUrl={homepageUrl}
                title={fallbackContent.title}
                message={fallbackContent.message}
                goToHomepageLabel="Go to Homepage"
                allRightsReservedLabel="All rights reserved."
            />
        </>
    );
}

export default function App({
    loaderData: {
        clientAuth,
        basketSnapshot,
        wishlistSummary,
        loyaltySummary,
        loyaltyEnabled,
        consentPolicy,
        analyticsUserProfile,
        getI18next,
        currency,
        correlationId,
        pageDesignerMode,
        site,
        locale,
        commerceEnabled,
        zendeskWebWidget,
        countrySuggestion,
    },
}: {
    loaderData: LoaderData;
}) {
    // Currency is always provided by loader (which reads from middleware)
    if (!currency) {
        throw new Error('Currency is required but not provided by loader');
    }

    // Get app configuration from server loader data (initial load) or window.__APP_CONFIG__ (client nav)
    // This ensures config is read from MRT environment variables (via middleware), not baked at build time
    const serverData = useRouteLoaderData('root');
    const appConfig = serverData?.appConfig || (typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined);
    if (!appConfig) {
        throw new Error('App configuration not available - check server loader and window.__APP_CONFIG__');
    }

    // In server-only auth architecture:
    // - clientAuth contains only non-sensitive fields (userType, customerId, usid, etc.)
    // - These values are serialized directly from the server loader
    // - No client middleware or bootstrap needed - server is the single source of truth
    // - Tokens (accessToken, refreshToken) stay server-side only

    const i18next = (typeof window === 'undefined' ? getI18next?.() : i18nextOnClient) as i18n;

    // Memoize the providers array to prevent unnecessary remounting of providers on render
    const providers = useMemo(
        () =>
            [
                [I18nextProvider, { i18n: i18next }],
                [ConfigProvider, { config: appConfig }],
                // Site provider will contain info about site/locale/currency on single request.
                // include i18next.language since these infos tend to go together.
                // site will drive the language/locale and currency
                [SiteProvider, { site, locale, language: i18next.language, currency }],
                [AuthProvider, { value: clientAuth }],
                [CommerceEnabledProvider, { enabled: commerceEnabled }],
                [BasketProvider, { snapshot: basketSnapshot }],
                [WishlistProvider, { initialWishlists: wishlistSummary }],
                [LoyaltyEnabledProvider, { enabled: loyaltyEnabled }],
                [ConsentPolicyProvider, { policy: consentPolicy }],
                [LoyaltyProvider, { initialSummary: loyaltySummary }],
                [AnalyticsUserProfileProvider, { initialProfile: analyticsUserProfile }],
                [CommerceFeedbackProvider, {}],
                [WishlistOverlayProvider, {}],
                [CountrySelectorProvider, {}],
                [CorrelationProvider, { value: correlationId }],
            ] as const,
        [
            correlationId,
            i18next,
            appConfig,
            currency,
            clientAuth,
            commerceEnabled,
            basketSnapshot,
            site,
            locale,
            wishlistSummary,
            loyaltySummary,
            loyaltyEnabled,
            consentPolicy,
            analyticsUserProfile,
        ]
    );

    const hybridEnabled = Boolean(appConfig?.hybrid?.enabled);

    return (
        <ComposeProviders providers={providers}>
            <UITargetProviders>
                <AuthActionExecutor />
                <CountrySuggestion lookup={countrySuggestion} />
                {hybridEnabled && <BackNavigationRevalidator />}
                <PageDesignerProvider
                    clientId="storefront-next"
                    targetOrigin="*"
                    usid={clientAuth?.usid}
                    mode={pageDesignerMode}>
                    <PageDesignerInit usid={clientAuth?.usid} />
                    <Outlet />
                </PageDesignerProvider>
                {typeof window !== 'undefined' && <ShoppingContextCookieSync />}
                {typeof window !== 'undefined' && <TrackingConsentSync />}
                {typeof window !== 'undefined' && <DataLayerTracker />}
                {typeof window !== 'undefined' && <PageViewTracker />}
                {zendeskWebWidget && <ZendeskWebWidgetController />}
            </UITargetProviders>
        </ComposeProviders>
    );
}

function ZendeskWebWidgetController() {
    const matches = useMatches();
    const hidden = shouldHideZendeskWebWidget(matches);

    useEffect(() => {
        const syncVisibility = () => {
            syncZendeskWebWidgetVisibility(hidden);
        };
        const script = document.getElementById('ze-snippet');

        if (!syncZendeskWebWidgetVisibility(hidden)) {
            script?.addEventListener('load', syncVisibility);
        }

        return () => {
            script?.removeEventListener('load', syncVisibility);
        };
    }, [hidden]);

    return null;
}

/**
 * Component that executes pending actions after authentication
 * This runs on every route to check if there are actions queued from auth interception
 */
function AuthActionExecutor() {
    useExecutePendingAction();
    return null;
}

/**
 * Revalidates loader data once on back/forward (e.g. back from SFRA). Mounted only when
 * hybrid is enabled; ensures UI is fresh after a full-page redirect. No-op when hybrid is off.
 */
function BackNavigationRevalidator() {
    const revalidator = useRevalidator();
    const didRevalidateRef = useRef(false);
    useEffect(() => {
        if (didRevalidateRef.current) return;
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'back_forward' && revalidator.state === 'idle') {
            didRevalidateRef.current = true;
            void revalidator.revalidate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}

/**
 * Initialize UITarget dev mode overlay (DEV ONLY - zero production overhead)
 * Lazy-loads the overlay when VITE_UI_TARGET_DEV_MODE=true
 */
function UITargetDevModeInit() {
    useEffect(() => {
        // Only runs in browser
        if (typeof window === 'undefined') return;

        // Only in development
        if (import.meta.env.PROD) return;

        // Only if enabled
        if (import.meta.env.VITE_UI_TARGET_DEV_MODE !== 'true') return;

        // Lazy load the overlay
        void import('@/lib/ui-target-dev-mode').then(({ initUITargetDevMode }) => {
            void initUITargetDevMode();
        });
    }, []);

    return null;
}
